/**
 * Purpose: {@link CopilotClient} — typed JSON-RPC 2.0 client for the
 *          @ada/copilot module. Wraps `fetch` so callers get
 *          method-by-method call signatures sourced from copilot's
 *          `MethodMap` instead of building envelopes by hand.
 *
 * Implementation notes:
 *   - Isomorphic by design: uses only `globalThis.fetch`,
 *     `crypto.randomUUID()`, and `AbortSignal`. No `node:*` imports.
 *   - All type imports from `@ada/copilot` go through `import type`. With
 *     `verbatimModuleSyntax: true` they erase from the emitted JS, so SPA
 *     bundlers don't pull in the dispatcher's node:async_hooks.
 *   - Error code mirroring: the small JSON-RPC + dispatcher error code
 *     enums are inlined here (rather than imported) so this client has
 *     zero runtime deps on @ada/dispatcher. Values must stay in lock-step
 *     with the upstream definitions.
 *   - One transport instance per client; cheap to instantiate. Reuse a
 *     client across requests so any future connection pooling (custom
 *     `fetch` injection) amortises across calls.
 *   - P0 surface: `health()` only. Future phases extend the client as
 *     more `copilot.*` methods are added (prefs, conversation, knowledge).
 *   - Transport translates JSON-RPC error envelopes to
 *     {@link CopilotClientError} with `kind: 'rpc'`; transport problems
 *     (HTTP 4xx/5xx, network errors, unparseable body) become
 *     `kind: 'transport'`.
 *
 * Created: 2026-05-08 17:00
 *
 * Changelog:
 *   - 2026-05-08 17:00 — Initial version. P0 surface — health() only.
 *   - 2026-05-09 17:00 — P3: add typed methods for the six
 *                        `copilot.conversations.*` endpoints. The client
 *                        now exposes a `conversations` namespace mirroring
 *                        the credits-client / api-keys-client shape.
 *   - 2026-05-22 — Pull through the upstream `ConversationsSaveParams`
 *                  (`metadata`) and `ConversationsListParams` (`filter`)
 *                  shape change. No code change here — the typed
 *                  signatures inherit the new optional fields directly
 *                  from `@ada/copilot`.
 */
import { CopilotClientError } from './errors.js';
/**
 * JSON-RPC 2.0 version literal. Inlined here so the client has no runtime
 * dep on @ada/dispatcher.
 */
const JSON_RPC_VERSION = '2.0';
class Transport {
    #url;
    #auth;
    #fetch;
    #headers;
    #id;
    constructor(opts) {
        this.#url = opts.url;
        this.#auth = opts.auth ?? { kind: 'none' };
        this.#fetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
        this.#headers = opts.headers ?? {};
        this.#id = opts.idGenerator ?? (() => crypto.randomUUID());
    }
    async call(method, params, callOpts = {}) {
        const id = callOpts.id ?? this.#id();
        const envelope = {
            jsonrpc: JSON_RPC_VERSION,
            method,
            params,
            id,
        };
        const res = await this.#post(envelope, method, callOpts);
        if (res.status === 204) {
            throw new CopilotClientError(`${method}: server returned 204 to a call (notifications-only response)`, { kind: 'transport', method, httpStatus: 204 });
        }
        const body = await this.#parseBody(res, method);
        if ('error' in body) {
            throw new CopilotClientError(`${method}: ${body.error.message}`, {
                kind: 'rpc',
                method,
                code: body.error.code,
                data: body.error.data,
                httpStatus: res.status,
            });
        }
        return body.result;
    }
    async #post(envelope, method, callOpts) {
        const headers = {
            'content-type': 'application/json',
            ...this.#headers,
            ...(callOpts.headers ?? {}),
        };
        const authHeader = await this.#authorizationHeader();
        if (authHeader)
            headers.authorization = authHeader;
        let res;
        try {
            res = await this.#fetch(this.#url, {
                method: 'POST',
                headers,
                body: JSON.stringify(envelope),
                signal: callOpts.signal,
            });
        }
        catch (err) {
            throw new CopilotClientError(`${method}: network error`, {
                kind: 'transport',
                method,
                cause: err,
            });
        }
        return res;
    }
    async #parseBody(res, method) {
        const text = await res.text();
        if (!res.ok && !text) {
            throw new CopilotClientError(`${method}: HTTP ${res.status}`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
            });
        }
        let parsed;
        try {
            parsed = JSON.parse(text);
        }
        catch (err) {
            throw new CopilotClientError(`${method}: response was not valid JSON (HTTP ${res.status})`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
                data: text.slice(0, 500),
                cause: err,
            });
        }
        if (parsed === null ||
            typeof parsed !== 'object' ||
            !('jsonrpc' in parsed)) {
            throw new CopilotClientError(`${method}: response was not a JSON-RPC envelope (HTTP ${res.status})`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
                data: parsed,
            });
        }
        return parsed;
    }
    async #authorizationHeader() {
        const auth = this.#auth;
        if (auth.kind === 'none')
            return undefined;
        if ('token' in auth)
            return `Bearer ${auth.token}`;
        const token = await auth.getToken();
        return token ? `Bearer ${token}` : undefined;
    }
}
// ---------------------------------------------------------------------------
// Client.
// ---------------------------------------------------------------------------
/**
 * Typed JSON-RPC client for the copilot module.
 *
 * @example
 * ```ts
 * const copilot = new CopilotClient({
 *   url: '/api',
 *   auth: { kind: 'bearer', getToken },
 * });
 * const status = await copilot.health();
 * console.log(status.version, status.serverTime);
 * ```
 */
export class CopilotClient {
    #transport;
    constructor(opts) {
        this.#transport = new Transport(opts);
    }
    /** Liveness probe. Returns the module version + server time. */
    health(params, callOpts) {
        return this.#transport.call('copilot.health', params ?? {}, callOpts);
    }
    /** Typed access to `copilot.conversations.*`. */
    conversations = {
        list: (params, callOpts) => this.#transport.call('copilot.conversations.list', params, callOpts),
        get: (params, callOpts) => this.#transport.call('copilot.conversations.get', params, callOpts),
        save: (params, callOpts) => this.#transport.call('copilot.conversations.save', params, callOpts),
        delete: (params, callOpts) => this.#transport.call('copilot.conversations.delete', params, callOpts),
        pin: (params, callOpts) => this.#transport.call('copilot.conversations.pin', params, callOpts),
        rename: (params, callOpts) => this.#transport.call('copilot.conversations.rename', params, callOpts),
    };
}
//# sourceMappingURL=client.js.map